import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  inject,
} from '@angular/core';
import { TranslatePipe } from '@kirigami/shared/i18n';

/**
 * Founder quote, per the landing refinement design: a blue gradient canvas
 * with oversized quotation marks, the quote itself and Vânia's photo anchored
 * to the bottom edge. Two motion layers, both optional:
 *
 * - a scroll-driven horizontal drift on the marks and the text (CSS scroll
 *   timelines, Chromium only);
 * - a pointer parallax where each `[data-px]`/`[data-py]` layer eases toward
 *   the cursor at its own rate (plain JS writing the `translate` property, so
 *   it composes with the drift's `transform` instead of overwriting it).
 *
 * Touch devices, reduced-motion users and browsers without scroll timelines
 * get the static composition.
 */
@Component({
  selector: 'app-quote-widget',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  template: `
    <section id="quote" class="quote" aria-labelledby="quote-text">
      <div class="quote__canvas">
        <img
          class="quote__mark quote__mark--left"
          src="img/quote-left.svg"
          alt=""
          aria-hidden="true"
          data-px="-38"
          data-py="-20"
        />
        <img
          class="quote__mark quote__mark--right"
          src="img/quote-right.svg"
          alt=""
          aria-hidden="true"
          data-px="-28"
          data-py="-15"
        />
        <blockquote class="quote__block" data-px="12" data-py="6">
          <p id="quote-text" class="quote__text">{{ 'landing.quote.text' | t }}</p>
          <footer class="quote__attribution">
            <span class="quote__name">{{ 'landing.quote.name' | t }}</span>
            <span class="quote__role">{{ 'landing.quote.role' | t }}</span>
          </footer>
        </blockquote>
        <!-- Photo held back for now. The .quote__photo rules and the section
             height it was sized against are left in place for when it returns.
        <img
          class="quote__photo"
          src="img/vania.webp"
          alt="Vânia Silva"
          width="1580"
          height="1579"
          decoding="async"
          data-px="18"
          data-py="9"
        />
        -->
      </div>
    </section>
  `,
  styles: [
    `
      .quote {
        position: relative;
        z-index: 1;
        height: 63rem;
        overflow: clip;
        /* One step deeper than the design's 500-to-400 so the white quote
           meets the 3:1 AA minimum for large text across the whole ramp. */
        background: linear-gradient(270deg, var(--kirigami-600) 0%, var(--kirigami-500) 100%);
      }

      .quote__role {
        font-weight: 400;
      }
      .quote__canvas {
        position: relative;
        width: 100%;
        max-width: 90rem;
        height: 100%;
        margin: 0 auto;
        /* The composition was laid out against a section that ended with the
           founder photo, so with the photo held back it sat in the top half.
           This drops all three layers together, keeping their relative
           geometry. Back to 0 when the photo returns. */
        --quote-shift: 12.9375rem;
      }
      .quote__mark {
        position: absolute;
        display: block;
      }
      .quote__mark--left {
        left: 26.295%;
        top: calc(3.125rem + var(--quote-shift));
        width: 14.25rem;
        height: auto;
      }
      .quote__mark--right {
        left: 68.722%;
        top: calc(28.25rem + var(--quote-shift));
        width: 7.0625rem;
        height: auto;
      }
      .quote__block {
        position: absolute;
        top: calc(10.4375rem + var(--quote-shift));
        left: 50%;
        transform: translateX(-50%);
        margin: 0;
        width: 48rem;
        max-width: calc(100% - 6rem);
        display: flex;
        flex-direction: column;
        gap: 1.9375rem;
        align-items: center;
      }
      .quote__text {
        margin: 0;
        font-size: 2rem;
        font-weight: 700;
        font-style: italic;
        line-height: 3rem;
        letter-spacing: 0.471px;
        text-align: center;
        color: var(--neu-0);
        text-wrap: pretty;
      }
      .quote__attribution {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        font-size: 1.25rem;
        /* Bold so 20px qualifies as large text for the AA contrast minimum. */
        font-weight: 700;
        line-height: 1;
        letter-spacing: 0.471px;
        text-align: center;
        color: var(--kirigami-50);
      }
      .quote__name {
        color: var(--neu-0);
      }
      .quote__photo {
        position: absolute;
        bottom: -7.3125rem;
        left: 50%;
        transform: translateX(-50%);
        /* 789px on the design's 1440px canvas is 54.8%, scaled to 75% so the
           photo's top edge clears the quote block. Keeping it proportional
           preserves the composition on narrower windows, and the bottom
           offset is scaled by the same factor so the same fraction of the
           photo stays cropped. */
        width: min(36.984375rem, 41.1%);
        height: auto;
        display: block;
        opacity: 0.6;
      }

      @media (max-width: 767px) {
        .quote {
          height: 45rem;
        }
        .quote__canvas {
          --quote-shift: 4rem;
        }
        .quote__mark--left {
          left: 8%;
          top: calc(2.5rem + var(--quote-shift));
          width: 7.5rem;
        }
        .quote__mark--right {
          left: 76%;
          top: calc(23.75rem + var(--quote-shift));
          width: 4rem;
        }
        .quote__block {
          top: calc(7.5rem + var(--quote-shift));
          max-width: calc(100% - 3rem);
        }
        .quote__text {
          font-size: 1.5rem;
          line-height: 2.25rem;
        }
        .quote__attribution {
          font-size: 1rem;
        }
        .quote__photo {
          bottom: -2.8125rem;
        }
      }

      /* Scroll drift: marks and text slide gently sideways as the section
         crosses the viewport. */
      @supports (animation-timeline: view()) {
        @media (prefers-reduced-motion: no-preference) {
          .quote {
            view-timeline: --quote-view block;
          }
          .quote__mark--left {
            will-change: transform;
            animation: quote-drift-l 1s linear both;
            animation-timeline: --quote-view;
            animation-range: cover 0% cover 100%;
          }
          .quote__mark--right {
            will-change: transform;
            animation: quote-drift-r 1s linear both;
            animation-timeline: --quote-view;
            animation-range: cover 0% cover 100%;
          }
          .quote__block {
            will-change: transform;
            animation: quote-drift-text 1s linear both;
            animation-timeline: --quote-view;
            animation-range: cover 0% cover 100%;
          }
        }
      }

      @keyframes quote-drift-l {
        from {
          transform: translate3d(-44px, 0, 0);
        }
        to {
          transform: translate3d(18px, 0, 0);
        }
      }
      @keyframes quote-drift-r {
        from {
          transform: translate3d(44px, 0, 0);
        }
        to {
          transform: translate3d(-18px, 0, 0);
        }
      }
      @keyframes quote-drift-text {
        from {
          transform: translateX(-50%) translate3d(12px, 0, 0);
        }
        to {
          transform: translateX(-50%) translate3d(-12px, 0, 0);
        }
      }
    `,
  ],
})
export class QuoteWidgetComponent implements AfterViewInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private raf: number | null = null;
  private onMove?: (event: PointerEvent) => void;
  private onLeave?: () => void;

  ngAfterViewInit(): void {
    if (typeof matchMedia === 'undefined') return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const section = this.host.nativeElement.querySelector<HTMLElement>('.quote');
    if (!section) return;
    const layers = Array.from(section.querySelectorAll<HTMLElement>('[data-px]')).map((el) => ({
      el,
      dx: parseFloat(el.dataset['px'] ?? '0'),
      dy: parseFloat(el.dataset['py'] ?? '0'),
      x: 0,
      y: 0,
    }));
    if (!layers.length) return;

    let targetX = 0;
    let targetY = 0;

    const tick = () => {
      let moving = false;
      for (const layer of layers) {
        layer.x += (targetX * layer.dx - layer.x) * 0.09;
        layer.y += (targetY * layer.dy - layer.y) * 0.09;
        layer.el.style.translate = `${layer.x.toFixed(2)}px ${layer.y.toFixed(2)}px`;
        if (
          Math.abs(targetX * layer.dx - layer.x) > 0.05 ||
          Math.abs(targetY * layer.dy - layer.y) > 0.05
        ) {
          moving = true;
        }
      }
      this.raf = moving ? requestAnimationFrame(tick) : null;
    };
    const start = () => {
      if (this.raf === null) this.raf = requestAnimationFrame(tick);
    };

    this.onMove = (event) => {
      const rect = section.getBoundingClientRect();
      const inside = event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) {
        if (targetX !== 0 || targetY !== 0) {
          targetX = 0;
          targetY = 0;
          start();
        }
        return;
      }
      targetX = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width - 0.5) * 2));
      targetY = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height - 0.5) * 2));
      start();
    };
    this.onLeave = () => {
      targetX = 0;
      targetY = 0;
      start();
    };

    window.addEventListener('pointermove', this.onMove, { passive: true });
    document.addEventListener('pointerleave', this.onLeave);
  }

  ngOnDestroy(): void {
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    if (this.onMove) window.removeEventListener('pointermove', this.onMove);
    if (this.onLeave) document.removeEventListener('pointerleave', this.onLeave);
  }
}
